import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'

import ContactInput from 'components/ContactInput'
import { addDoc, deleteDoc, getDocs, updateDoc, where } from 'lib/firestore'

const convert = data => {
  const { contact, ...values } = data
  return { ...values, ...contact }
}

export default function LocationDialog({ account, data, onClose }) {
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: convert(data) })
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(true)
  }, [])

  const onSubmit = async values => {
    const { name, category, ...contact } = values
    if (data.id) {
      return updateDoc(`accounts/${account.id}/locations/${data.id}`, {
        name,
        category,
        contact,
      }).then(() => setOpen(false))
    } else {
      return addDoc(`accounts/${account.id}/locations`, {
        name,
        category,
        contact,
        deleted: false,
      }).then(() => setOpen(false))
    }
  }

  const handleToggleArchive = async deleted => {
    await updateDoc(`accounts/${account.id}/locations/${data.id}`, { deleted })
    setOpen(false)
  }

  const handleDelete = async () => {
    // 在庫が登録されている拠点は削除できない
    const stocks = await getDocs(
      `accounts/${account.id}/stocks`,
      where('location.id', '==', data.id)
    )
    if (stocks.length > 0) {
      return setError('common', {
        message: '在庫が登録されている拠点は削除できません。',
      })
    }

    // メンバーが紐付いている拠点は削除できない
    const members = await getDocs(
      `accounts/${account.id}/members`,
      where('locationId', '==', data.id)
    )
    if (members.length > 0) {
      return setError('common', {
        message: 'メンバーが紐付いている拠点は削除できません。',
      })
    }

    await deleteDoc(`accounts/${account.id}/locations/${data.id}`)
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} TransitionProps={{ onExited: onClose }}>
      <DialogTitle>拠点{data.id ? '情報' : '追加'}</DialogTitle>
      <DialogContent>
        <Controller
          name="name"
          control={control}
          rules={{ required: true, maxLength: 20 }}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ mb: 2 }}
              label="拠点名"
              placeholder="〇〇支社"
              disabled={isSubmitting}
              helperText={errors.name && '20 文字以内で入力してください'}
            />
          )}
        />
        <Controller
          name="category"
          control={control}
          rules={{ maxLength: 20 }}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ mb: 2 }}
              label="拠点カテゴリ"
              placeholder="オプション（ソート用）"
              disabled={isSubmitting}
              helperText={errors.category && '20 文字以内で入力してください'}
            />
          )}
        />
        <ContactInput control={control} disalbed={isSubmitting} errors={errors} />
        {errors.common && <FormHelperText error>{errors.common.message}</FormHelperText>}
      </DialogContent>
      <DialogActions sx={{ py: 1, px: 3 }}>
        {data.id && (
          <Button
            color={data.deleted ? 'primary' : 'secondary'}
            onClick={() => handleToggleArchive(!data.deleted)}
          >
            {data.deleted ? '復元する' : '削除する'}
          </Button>
        )}
        {data.id && data.deleted && (
          <Button color="secondary" onClick={handleDelete}>
            完全に削除
          </Button>
        )}
        <div style={{ flex: 1 }} />
        <Button onClick={() => setOpen(false)}>キャンセル</Button>
        <Button color="primary" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {data.id ? '更新する' : '追加する'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
